import React, { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  IconClose,
  IconArrowRight,
  IconCountry,
  IconEdit,
  IconLink,
  useWindowDimensions
} from '@Knowledge-OTP/znk-ui-components'
import { useSelector, useDispatch } from 'react-redux'
import { CustomInput } from 'reactstrap'
import { ReactComponent as IconRemove } from '../../../../assets/IconRemove.svg'
import useTranslation from '../../../../i18n/useTranslation'
import moment from 'moment-timezone'
import './index.css'

const getTopic = (lesson) => lesson?.individualTopic || lesson?.planTopic || lesson?.groupTopic || {}

const getLessonType = (lesson) => {
  const type = lesson?.__typename?.replace('Lesson', '') || 'Lesson'
  return type.replace(/([A-Z])/g, ' $1').trim()
}

const getEducatorInitials = (lesson) => {
  const firstName = lesson?.educator?.firstName || ''
  const lastName = lesson?.educator?.lastName || ''
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '—'
}

const getLessonName = (lesson) => {
  const topic = getTopic(lesson)
  return lesson?.service?.name || topic?.name || ''
}

const getMobileTitle = (lesson) => {
  if (lesson?.title) return lesson.title
  const name = getLessonName(lesson)
  const type = `${getLessonType(lesson)}`.toLowerCase()
  return name ? `${name} · ${type.charAt(0).toUpperCase()}${type.slice(1)}` : type
}

const formatMobileDate = (date, zone) => {
  const value = zone ? moment(date).tz(zone) : moment(date)
  return value.format('MMM D, h:mma').replace(/m$/, '')
}

const RescheduledPendingLessons = (props) => {
  const { lessons = [], refetchPending = () => {} } = props
  const { isOpen } = useSelector((state) => state.modal.lesson.rescheduledpending)
  const { selectedUserData: selectedUserDataRedux, selectedArrayUserData } = useSelector((state) => state.modal.calendar.selectUser)
  const selectedUserData = selectedArrayUserData[0] || selectedUserDataRedux || {}
  const dispatch = useDispatch()
  const { translate } = useTranslation()
  const zone = useSelector((state) => state.timezone.zone)
  const [selectedIds, setSelectedIds] = useState([])
  const { breakWidth } = useWindowDimensions()
  const isMobile = breakWidth === 'SM'

  const selectedLessons = useMemo(
    () => lessons.filter((lesson) => selectedIds.includes(lesson.id)),
    [lessons, selectedIds]
  )
  const allSelected = lessons.length > 0 && selectedLessons.length === lessons.length
  const someSelected = selectedLessons.length > 0 && !allSelected
  const canEditTogether = selectedLessons.length > 1 && selectedLessons.every((lesson) => {
    const first = selectedLessons[0]
    const lessonTopic = getTopic(lesson)
    const firstTopic = getTopic(first)
    return lesson?.service?.id && lesson?.service?.id === first?.service?.id &&
      lessonTopic?.id && lessonTopic?.id === firstTopic?.id
  })

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => lessons.some((lesson) => lesson.id === id)))
  }, [lessons])

  useEffect(() => {
    if (!isOpen) setSelectedIds([])
  }, [isOpen])

  const toggle = () => {
    dispatch({
      type: 'SET_DATA_MODAL_FORM_LIST',
      payload: 'lesson.rescheduledpending',
      data: { isOpen: !isOpen }
    })
  }

  const toggleSelection = (id) => {
    setSelectedIds((current) => current.includes(id)
      ? current.filter((selectedId) => selectedId !== id)
      : [...current, id])
  }

  const openLesson = (lesson) => {
    toggle()
    const isOrientation = lesson?.service?.id === orientation?.id
    dispatch({
      type: 'SET_DATA_MODAL_FORM_LIST',
      payload: `plan.${isOrientation ? 'orientationLessonModal' : 'infoLessonModal'}`,
      data: { isOpen: true, dataLesson: lesson }
    })
  }

  const services = useSelector((state) => state.user.services)
  const orientation = services?.find((service) => service.isOrientation)

  const startBulkEdit = () => {
    if (!canEditTogether) return
    const firstLesson = selectedLessons[0]
    toggle()
    dispatch({
      type: 'SET_DATA_MODAL_FORM_LIST',
      payload: 'lesson.editLessonModal',
      data: {
        isOpen: true,
        dataLesson: firstLesson,
        editPending: true,
        selectedLessons,
        refetch: () => {
          refetchPending()
          dispatch({
            type: 'SET_DATA_MODAL_FORM_LIST',
            payload: 'lesson.rescheduledpending',
            data: { isOpen: true }
          })
        }
      }
    })
  }

  const removeSelected = () => {
    dispatch({
      type: 'SET_DATA_MODAL_FORM_LIST',
      payload: 'lesson.cancelMultiple',
      data: {
        isOpen: true,
        lessonsId: selectedLessons.map((lesson) => lesson.id),
        refetch: () => {
          setSelectedIds([])
          refetchPending()
        }
      }
    })
  }

  const timezoneLabel = zone || 'Timezone'
  const selectedTopics = [...new Set(selectedLessons.map((lesson) => getLessonName(lesson)).filter(Boolean))]

  const renderMobileFooter = () => (
    <div className='pending-lessons-mobile-footer'>
      {selectedLessons.length === 0 && (
        <button type='button' className='pending-lessons-mobile-btn is-disabled' disabled>
          Select a lesson to continue
        </button>
      )}
      {selectedLessons.length === 1 && (
        <button type='button' className='pending-lessons-mobile-btn primary is-single' onClick={() => openLesson(selectedLessons[0])}>
          <span>Go to lesson</span>
          <IconArrowRight size={14} className='' />
        </button>
      )}
      {selectedLessons.length > 1 && (
        <>
          <div className='pending-lessons-mobile-selection'>
            <span className='pending-lessons-mobile-count'>{selectedLessons.length}</span>
            lessons selected
          </div>
          {!canEditTogether && (
            <div className='pending-lessons-mobile-hint'>
              To edit together, select lessons from one service topic. Selected: {selectedTopics.join(', ')}.
            </div>
          )}
          <div className='pending-lessons-mobile-actions'>
            <button
              type='button'
              className={`pending-lessons-mobile-btn ${canEditTogether ? 'primary' : 'is-disabled'}`}
              disabled={!canEditTogether}
              onClick={startBulkEdit}
            >
              <IconEdit size={14} /> Edit Lessons
            </button>
            <button type='button' className='pending-lessons-mobile-btn remove' onClick={removeSelected}>
              <IconRemove /> Remove
            </button>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Modal
      backdrop='static'
      buttonLabel={null}
      className={`modal-colored ${isMobile ? 'modal-botbar is-mobile' : 'modal-centered'} fixed-head-modal pending-lessons-modal w-200`}
      isOpen={isOpen}
      toggle={toggle}
      Head={
        <div className='pending-lessons-header-wrapper'>
          {isMobile && <div className='pending-lessons-handle' />}
          <div className='pending-lessons-header'>
            <div>
              <h2 className='pending-lessons-title'>
                {translate('modal.infolesson.haspending', {
                  name: selectedUserData?.firstName,
                  num: lessons.length
                })}
              </h2>
              <p className='pending-lessons-subtitle'>
                {isMobile
                  ? 'Tap a lesson to open it. Use the checkboxes to edit or remove several.'
                  : 'Click a lesson to open it. Use the checkboxes to select several and edit or remove them together.'}
              </p>
            </div>
            <button type='button' className='pending-lessons-close' aria-label='Close' onClick={toggle}>
              <IconClose size={14} />
            </button>
          </div>
        </div>
      }
    >
      <div className='pending-lessons-content'>
        <button
          type='button'
          className='pending-lessons-timezone'
          onClick={() => dispatch({
            type: 'SET_DATA_MODAL_FORM_LIST',
            payload: 'lesson.changeTimezoneModal',
            data: { isOpen: true }
          })}
        >
          <IconCountry />
          <span>Times shown in {timezoneLabel}</span>
          <IconArrowRight className='pending-lessons-timezone-arrow' />
        </button>

        <div
          className='pending-lessons-select-all'
        >
          <CustomInput
            type='checkbox'
            id='pending-lessons-select-all'
            className='pending-lessons-select-all-control'
            checked={allSelected}
            aria-checked={someSelected ? 'mixed' : allSelected ? 'true' : 'false'}
            innerRef={(input) => {
              if (input) input.indeterminate = someSelected
            }}
            onChange={() => setSelectedIds(allSelected ? [] : lessons.map((lesson) => lesson.id))}
            label={<span>Select all · {lessons.length} pending</span>}
          />
        </div>

        <div className='pending-lessons-list' role='list'>
          {lessons.map((lesson) => {
            const isSelected = selectedIds.includes(lesson.id)
            const topic = getTopic(lesson)
            const duration = moment.duration(moment(lesson.endDate).diff(moment(lesson.startDate))).asMinutes()
            return (
              <div
                key={lesson.id}
                role='listitem'
                tabIndex={0}
                className={`pending-lesson-row ${isSelected ? 'is-selected' : ''}`}
                onClick={() => openLesson(lesson)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openLesson(lesson)
                  }
                }}
              >
                <div
                  className='pending-lessons-check-hitarea'
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomInput
                    type='checkbox'
                    id={`pending-lesson-${lesson.id}`}
                    className='pending-lesson-checkbox-control'
                    checked={isSelected}
                    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${lesson.title || topic?.name || 'lesson'}`}
                    onChange={() => toggleSelection(lesson.id)}
                    label=''
                  />
                </div>
                {isMobile
                  ? (
                    <div className='pending-lesson-main'>
                      <div className='pending-lesson-title'>{getMobileTitle(lesson)}</div>
                      <div className='pending-lesson-meta'>
                        {formatMobileDate(lesson.startDate, zone)} · {duration} min
                      </div>
                    </div>
                  )
                  : (
                    <div className='pending-lesson-main'>
                      <div className='pending-lesson-title'>{lesson.title || topic?.name || lesson?.service?.name}</div>
                      <div className='pending-lesson-meta'>
                        {moment(lesson.startDate).tz(zone).format('MMM D, YYYY h:mma')} · {duration} min
                      </div>
                      <div className='pending-lesson-topic'>
                        <IconLink size={10} /> {lesson?.service?.name || topic?.name}  {getLessonType(lesson)}
                      </div>
                    </div>
                  )}
                <div className='pending-lesson-educator' aria-label={lesson?.educator?.firstName || 'Educator'}>
                  {getEducatorInitials(lesson)}
                </div>
              </div>
            )
          })}
        </div>

        {isMobile && renderMobileFooter()}

        {!isMobile && selectedLessons.length === 1 && (
          <button type='button' className='pending-lessons-mobile-btn primary is-single' onClick={() => openLesson(selectedLessons[0])}>
            <span>Go to lesson</span>
            <IconArrowRight size={14} className='' />
          </button>
        )}

        {!isMobile && selectedIds.length > 1 && (
          <div className={`pending-lessons-actionbar ${selectedIds.length > 1 && !canEditTogether ? 'has-mixed-selection' : ''}`}>
            <div className='pending-lessons-selection'>
              <span className='pending-lessons-count'>{selectedIds.length}</span>
              <span>{selectedIds.length === 1 ? 'lesson selected' : 'lessons selected'}</span>
              <button type='button' className='pending-lessons-count-close' aria-label='Clear selection' onClick={() => setSelectedIds([])}>
                <IconClose size={12} />
              </button>
            </div>
            {selectedIds.length === 1
              ? (
                <button type='button' className='pending-lessons-action primary' onClick={() => openLesson(selectedLessons[0])}>
                  {translate('modal.infolesson.continue')} <IconArrowRight />
                </button>
              )
              : (
                <div className='pending-lessons-bulk-actions'>
                  <button
                    type='button'
                    className='pending-lessons-action edit'
                    disabled={!canEditTogether}
                    title={!canEditTogether ? 'Select lessons with the same service and topic to edit together.' : ''}
                    onClick={startBulkEdit}
                  >
                    <IconEdit /> Edit Lessons
                  </button>
                  <span className='pending-lessons-action-divider' />
                  <button type='button' className='pending-lessons-action remove' onClick={removeSelected}>
                    <IconRemove /> Remove
                  </button>
                </div>
              )}
            {selectedIds.length > 1 && !canEditTogether && (
              <div className='pending-lessons-edit-hint'>Select lessons with the same service and topic to edit together.</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default RescheduledPendingLessons
